import { SflActionResponseSchema } from "../schemas/save-for-later";

/**
 * COM-63 — pure settle-classification for the Save-for-Later flows, kept out
 * of `BasketProvider` / `SaveForLaterProvider` so the rules are unit-testable
 * without rendering either.
 */

export type SaveLineItemOutcome =
  /** SFL add ok AND basket line removed. */
  | "success"
  /**
   * The product was ALREADY on the server SFL list (`addToSaveForLater`
   * returns `{ ok: false, reason: "DUPLICATE" }` without throwing) and the
   * basket line was still removed — the move DID complete. Not a failure.
   * Common now that the SFL list only loads on /cart, so the minicart can't
   * know the product is already saved.
   */
  | "duplicate"
  /** SFL add ok (or duplicate) but the basket-remove leg failed — item in both. */
  | "partial"
  /** validationError, contained `sfl-error`, shape drift, or wrong `kind`. */
  | "failed";

export function classifySaveLineItemSettle(data: unknown): SaveLineItemOutcome {
  const parsed = SflActionResponseSchema.safeParse(data);
  if (!parsed.success || parsed.data.kind !== "savedLineItem") return "failed";
  if (parsed.data.partial) return "partial";
  if (!parsed.data.result.ok) return "duplicate";
  return "success";
}

/** Outcomes where the cart line is back / never left, so the snap must drop
 *  and the shopper must be told. */
export function saveLineItemNeedsRollback(
  outcome: SaveLineItemOutcome,
): boolean {
  return outcome === "partial" || outcome === "failed";
}

/**
 * Guest "Save for later" writes localStorage FIRST, then removes the basket
 * line; a failed remove leg rolls the write back. But `guestAdd` on an id that
 * is ALREADY saved is a no-op success — rolling THAT back would delete a
 * pre-existing saved entry (data loss). Only roll back what this save wrote.
 */
export function shouldRollbackGuestSflWrite(pending: {
  alreadySaved: boolean;
}): boolean {
  return !pending.alreadySaved;
}

/**
 * The productId a contained SFL submission failure was about, when the
 * payload is the clientAction's `sfl-error` envelope. `undefined` for a
 * server validationError (which carries no typed productId).
 */
export function failedSflProductId(data: unknown): string | undefined {
  const parsed = SflActionResponseSchema.safeParse(data);
  if (!parsed.success || parsed.data.kind !== "sfl-error") return undefined;
  return parsed.data.productId || undefined;
}
